class PawKasaTheme {
  constructor() {
    const stickyAtc = document.querySelector('.sticky-atc');
    if (stickyAtc) {
      document.body.classList.add('has-sticky-atc');
      const setStickyHeight = () => {
        document.body.style.setProperty('--sticky-atc-height', `${stickyAtc.offsetHeight}px`);
      };
      setStickyHeight();
      window.addEventListener('resize', setStickyHeight);
    }
    this.bindMobileMenu();
    this.bindCartDrawer();
    this.bindProductForms();
    this.bindSubscriptionPopup();
    this.bindCollectionFilters();
    this.hideStaleCollectionPagination();
    this.bindProductGallery();
    this.bindProductRecommendations();
    this.bindStickyAddToCart();
  }

  bindMobileMenu() {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    toggle?.addEventListener('click', () => menu?.classList.toggle('is-open'));
  }

  bindCartDrawer() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.overlay = document.querySelector('[data-cart-overlay]');
    document.querySelectorAll('[data-cart-open]').forEach((button) => button.addEventListener('click', () => this.openCart()));
    document.querySelectorAll('[data-cart-close], [data-cart-overlay]').forEach((button) => button.addEventListener('click', () => this.closeCart()));
    document.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-cart-change]');
      if (!button) return;
      await this.changeCart(button.dataset.key, Number(button.dataset.quantity));
    });
  }

  bindProductForms() {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('form[action*="/cart/add"]');
      if (!form || form.dataset.productForm === '') return;
      event.preventDefault();
      const formData = new FormData(form);
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (response.ok) {
        await this.refreshCart();
        this.openCart();
      } else {
        form.submit();
      }
    });
  }

  bindSubscriptionPopup() {
    const popup = document.querySelector('[data-subscription-popup]');
    if (!popup) return;

    const sessionKey = 'pawkasa_newsletter_shown_session';
    const dismissedKey = 'pawkasa_newsletter_dismissed_at';
    const subscribedKey = 'pawkasa_newsletter_subscribed_at';
    const dayMs = 24 * 60 * 60 * 1000;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const storage = {
      get(store, key) {
        try {
          return store.getItem(key);
        } catch {
          return null;
        }
      },
      set(store, key, value) {
        try {
          store.setItem(key, value);
        } catch {
          // Storage can be blocked in private browsing; the popup still works for this page view.
        }
      },
    };
    const settings = {
      delaySeconds: Number(popup.dataset[isMobile ? 'mobileDelaySeconds' : 'desktopDelaySeconds']) || (isMobile ? 10 : 6),
      scrollPercent: Number(popup.dataset[isMobile ? 'mobileScrollPercent' : 'desktopScrollPercent']) || (isMobile ? 35 : 40),
      dismissDays: Number(popup.dataset[isMobile ? 'mobileDismissDays' : 'desktopDismissDays']) || (isMobile ? 14 : 7),
      subscribedDays: Number(popup.dataset.subscribedDays) || 90,
    };
    let hasOpened = false;
    let delayTimer;

    if (!document.body.classList.contains('template-index')) return;

    if (popup.querySelector('.subscription-popup__message--success')) {
      storage.set(localStorage, subscribedKey, String(Date.now()));
      storage.set(sessionStorage, sessionKey, 'true');
      return;
    }

    const withinCooldown = (key, days) => {
      const storedAt = Number(storage.get(localStorage, key));
      return storedAt && Date.now() - storedAt < days * dayMs;
    };

    const hasCartItems = async () => {
      const visibleCount = Number(document.querySelector('[data-cart-count]')?.textContent || 0);
      if (visibleCount > 0) return true;
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return false;
        const cart = await response.json();
        return Number(cart.item_count) > 0;
      } catch {
        return false;
      }
    };

    const shouldSkipPopup = async () => {
      if (storage.get(sessionStorage, sessionKey) === 'true') return true;
      if (withinCooldown(subscribedKey, settings.subscribedDays)) return true;
      if (withinCooldown(dismissedKey, settings.dismissDays)) return true;
      return hasCartItems();
    };

    const openPopup = () => {
      if (hasOpened || popup.classList.contains('is-open')) return;
      hasOpened = true;
      storage.set(sessionStorage, sessionKey, 'true');
      popup.hidden = false;
      document.body.classList.add('subscription-popup-open');
      requestAnimationFrame(() => popup.classList.add('is-open'));
    };
    const closePopup = (rememberDismissal = true) => {
      if (rememberDismissal) storage.set(localStorage, dismissedKey, String(Date.now()));
      popup.classList.remove('is-open');
      document.body.classList.remove('subscription-popup-open');
      window.setTimeout(() => {
        popup.hidden = true;
      }, 300);
    };

    const tryOpenPopup = async () => {
      if (await shouldSkipPopup()) return;
      openPopup();
    };

    const handleScrollTrigger = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const scrollPercent = (window.scrollY / scrollable) * 100;
      if (scrollPercent >= settings.scrollPercent) {
        window.removeEventListener('scroll', handleScrollTrigger);
        window.clearTimeout(delayTimer);
        tryOpenPopup();
      }
    };

    delayTimer = window.setTimeout(tryOpenPopup, settings.delaySeconds * 1000);
    window.addEventListener('scroll', handleScrollTrigger, { passive: true });

    if (!isMobile) {
      document.addEventListener('mouseleave', (event) => {
        if (event.clientY > 8) return;
        window.removeEventListener('scroll', handleScrollTrigger);
        window.clearTimeout(delayTimer);
        tryOpenPopup();
      }, { once: true });
    }

    popup.querySelectorAll('[data-subscription-close]').forEach((trigger) => {
      trigger.addEventListener('click', () => closePopup(true));
    });
    popup.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePopup(true);
    });
    popup.querySelector('form')?.addEventListener('submit', () => {
      storage.set(sessionStorage, sessionKey, 'true');
      window.setTimeout(() => closePopup(false), 250);
    });
  }

  bindCollectionFilters() {
    const triggers = document.querySelectorAll('[data-filter-trigger]');
    const selects = document.querySelectorAll('[data-filter-select]');
    const segments = document.querySelectorAll('[data-filter-segment]');
    const moreToggle = document.querySelector('.more-toggle');
    const moreMenu = document.querySelector('.more-menu');
    const cards = document.querySelectorAll('[data-product-card]');
    if ((!triggers.length && !selects.length && !segments.length) || !cards.length) return;

    const applyFilter = (filter) => {
      const value = filter || 'All';

      triggers.forEach((trigger) => {
        trigger.classList.toggle('is-active', trigger.dataset.filter === value);
      });
      selects.forEach((select) => {
        select.value = value;
      });
      segments.forEach((segment) => {
        segment.classList.toggle('active', segment.dataset.filterSegment === value);
      });
      cards.forEach((card) => {
        const isVisible = value === 'All' || card.dataset.category === value;
        card.hidden = !isVisible;
      });

      const url = new URL(window.location.href);
      if (value === 'All') {
        url.searchParams.delete('filter');
      } else {
        url.searchParams.set('filter', value);
      }
      window.history.replaceState({}, '', url);

      moreMenu?.classList.remove('open');
      moreToggle?.setAttribute('aria-expanded', 'false');
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        applyFilter(trigger.dataset.filter || 'All');
        document.querySelector('#ProductGrid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    selects.forEach((select) => {
      select.addEventListener('change', () => {
        applyFilter(select.value || 'All');
        document.querySelector('#ProductGrid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    segments.forEach((segment) => {
      segment.addEventListener('click', () => {
        applyFilter(segment.dataset.filterSegment || 'All');
        document.querySelector('#ProductGrid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    if (moreToggle && moreMenu) {
      moreToggle.addEventListener('click', (event) => {
        event.stopPropagation();
        const isOpen = moreMenu.classList.toggle('open');
        moreToggle.setAttribute('aria-expanded', String(isOpen));
      });
      document.addEventListener('click', () => {
        moreMenu.classList.remove('open');
        moreToggle.setAttribute('aria-expanded', 'false');
      });
      moreMenu.addEventListener('click', (event) => event.stopPropagation());
    }

    const initialFilter = new URL(window.location.href).searchParams.get('filter');
    if (initialFilter) {
      applyFilter(initialFilter);
    }
  }

  hideStaleCollectionPagination() {
    document.querySelector('.collection-page > .pagination')?.remove();
  }

  bindProductGallery() {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-gallery-main]');
      if (!main) return;
      gallery.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
        button.addEventListener('click', () => {
          const src = button.dataset.fullSrc;
          if (!src) return;
          main.src = src;
          main.removeAttribute('srcset');
          gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => thumb.classList.toggle('is-active', thumb === button));
        });
      });
    });
  }

  async bindProductRecommendations() {
    const container = document.querySelector('[data-product-recommendations]');
    if (!container?.dataset.url) return;
    const response = await fetch(container.dataset.url);
    if (!response.ok) return;
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const updated = doc.querySelector('[data-product-recommendations]');
    if (updated?.querySelector('.product-card')) container.innerHTML = updated.innerHTML;
  }

  bindStickyAddToCart() {
    const sticky = document.querySelector('[data-sticky-atc]');
    const hero = document.querySelector('.pdp-hero');
    if (!sticky || !hero || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      sticky.classList.toggle('is-visible', !entry.isIntersecting);
    }, { threshold: 0 });
    observer.observe(hero);
  }

  async changeCart(key, quantity) {
    await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    await this.refreshCart();
  }

  async refreshCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    const cart = await response.json();
    this.renderCart(cart);
  }

  renderCart(cart) {
    const drawerItems = document.querySelector('[data-cart-items]');
    const count = document.querySelector('[data-cart-count]');
    const subtotal = document.querySelector('[data-cart-subtotal]');
    if (count) count.textContent = cart.item_count;
    if (subtotal) subtotal.textContent = this.formatMoney(cart.total_price);
    if (!drawerItems) return;

    if (!cart.item_count) {
      drawerItems.innerHTML = `
        <div class="empty-cart">
          <h3>Your cart is empty</h3>
          <p>Add a scratcher or two, then checkout with Shopify secure checkout.</p>
          <a class="button button--primary" href="${window.Shopify.routes.root}collections/all">Shop products</a>
        </div>
      `;
      return;
    }

    drawerItems.innerHTML = cart.items.map((item) => `
      <div class="cart-line" data-line-key="${item.key}">
        <img src="${item.image || ''}" alt="${this.escapeHtml(item.product_title)}" loading="lazy">
        <div>
          <h3>${this.escapeHtml(item.product_title)}</h3>
          <p>${this.formatMoney(item.final_line_price)}</p>
          <div class="qty-controls">
            <button type="button" data-cart-change data-key="${item.key}" data-quantity="${Math.max(0, item.quantity - 1)}">-</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-change data-key="${item.key}" data-quantity="${item.quantity + 1}">+</button>
          </div>
        </div>
      </div>
    `).join('');
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify.currency?.active || 'USD',
    }).format((cents || 0) / 100);
  }

  escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    }[char]));
  }

  openCart() {
    this.drawer?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
  }

  closeCart() {
    this.drawer?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
  }
}

document.addEventListener('DOMContentLoaded', () => new PawKasaTheme());
